import { httpClient } from 'app/api/http/http-client';
import { fileBaseURL } from 'app/constants/enviroments';
import { AxiosRequestConfig } from 'axios';

export interface FileUploadAccessTokenResponse {
  token: string;
  business_key: string;
}

export interface FileUploadConfig {
  endpoint: string;
  baseURL?: string;
  headers?: Record<string, string>;
  origin?: string;
  getAccessToken?(): Promise<FileUploadAccessTokenResponse>;
}

export interface FileUploadResponse {
  data: {
    [key: string]: any;
    file_name: string;
  };
  success: boolean;
}

/**
 * Global file upload service that can be used throughout the application
 * @param data - FormData containing the file(s) to upload
 * @param config - Configuration object for the upload request
 * @returns Promise with the upload response
 */
export const uploadFile = async (
  data: FormData,
  config: FileUploadConfig
): Promise<FileUploadResponse> => {
  const { endpoint, baseURL = fileBaseURL, getAccessToken, headers = {}, origin } = config;

  // Get access token if token getter is provided
  let token: string | undefined;
  if (getAccessToken) {
    const tokenResponse = await getAccessToken();
    token = tokenResponse.token;
  }

  // Prepare headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'multipart/form-data',
    Accept: '*/*',
    ...headers,
  };

  // Add authorization token if available
  if (token) {
    requestHeaders.Authorization = token;
  }

  // Add origin if provided
  if (origin) {
    requestHeaders.Origin = origin;
  }

  // Prepare request config
  const requestConfig: AxiosRequestConfig = {
    baseURL,
    headers: requestHeaders,
  };
  // Note: httpClient interceptor already returns response.data, so the response is the data directly
  const response = await httpClient.put<FileUploadResponse, FormData, FileUploadResponse>(
    endpoint,
    data,
    requestConfig
  );

  return response;
};

/**
 * Helper function to get access token for file uploads
 * This is a common pattern used across the application
 */
export const getFileUploadAccessToken = async (): Promise<FileUploadAccessTokenResponse> => {
  return await httpClient.post(`/api-web/v1/main/get_my_token`);
};
